import React, { useCallback, useState } from "react";
import { Form, Row, Col, DatePicker, Select, message } from "antd";
import moment from "moment";
import { LoadTimeSlot, LoadTimeSlotMap } from "@/utils/enum";
import { changeAdminETARequest } from "@/services/loads";
import { disableDatesBeforeToday } from "@/utils/utils";

function convertMilHours(hour) {
  if (hour === 0 || hour === 12) {
    return 12;
  }

  return hour % 12;
}

export default function ETAForm({ formRef, shipmentData, setIsLoadingDelivery, refreshData }) {
  const [pickupEtaDateSet, setPickupEtaDateSet] = useState(true);
  const [dropoffEtaDateSet, setDropoffEtaDateSet] = useState(true);

  const parseInitialEtaTime = useCallback((etaDateString, etaTimeSlotIndex) => {
    if (etaTimeSlotIndex !== null && etaTimeSlotIndex !== undefined) {
      return Number(etaTimeSlotIndex);
    }

    if (!etaDateString) return null;

    const etaDateStart = moment.utc(etaDateString).locale("en").local();

    const etaDateEnd = moment(etaDateStart).add(4, "hours");

    const etaHoursStartMil = etaDateStart.get("hours");
    const etaHoursStart = convertMilHours(etaHoursStartMil);
    const etaHoursEndMil = etaDateEnd.get("hours");
    const etaHoursEnd = convertMilHours(etaHoursEndMil);

    const startAMOrPM = etaHoursStartMil < 12 ? "AM" : "PM";
    const endAMOrPM = etaHoursEndMil < 12 ? "AM" : "PM";

    return `${etaHoursStart}${startAMOrPM} to ${etaHoursEnd}${endAMOrPM}`;
  }, []);

  const handlePickupEtaDateChange = useCallback(
    async value => {
      if (!value) {
        setPickupEtaDateSet(false);
        formRef?.setFieldsValue({
          pickupEtaTimeSlotIndex: null,
        });
        return;
      }

      setPickupEtaDateSet(true);
    },
    [shipmentData?.id, formRef],
  );

  const handleDropoffEtaDateChange = useCallback(
    value => {
      if (!value) {
        setDropoffEtaDateSet(false);
        formRef?.setFieldsValue({
          dropoffEtaTimeSlotIndex: null,
        });
        return;
      }

      setDropoffEtaDateSet(true);
    },
    [formRef],
  );

  const onSubmit = useCallback(
    values => {
      const loadId = shipmentData?.load?.id;
      if (!loadId) return;

      const { pickupEtaDate, dropoffEtaDate, pickupEtaTimeSlotIndex, dropoffEtaTimeSlotIndex } = values;

      if (pickupEtaDate?.isAfter(dropoffEtaDate, "day")) {
        message.error("Drop Off time cannot be before pickup time.");
        return;
      }

      if (
        pickupEtaDate?.isSame(dropoffEtaDate, "day") &&
        Number(pickupEtaTimeSlotIndex) >= Number(dropoffEtaTimeSlotIndex)
      ) {
        message.error("Drop Off time cannot be before pickup time.");
        return;
      }

      setIsLoadingDelivery(true);

      const payload = {
        pickupETA: pickupEtaDate?.startOf("day"),
        dropOffETA: dropoffEtaDate?.startOf("day"),
        pickupEtaTimeSlot: pickupEtaTimeSlotIndex,
        dropOffEtaTimeSlot: dropoffEtaTimeSlotIndex,
      };

      changeAdminETARequest(loadId, payload)
        .then(() => {
          message.success("ETA dates updated!");
          setIsLoadingDelivery(false);
          if (typeof refreshData === "function") refreshData();
        })
        .catch(err => {
          console.error(err);
          message.error("Could not update ETA");
          setIsLoadingDelivery(false);
        });
    },
    [shipmentData, setIsLoadingDelivery, refreshData],
  );

  return (
    <Form
      layout="vertical"
      form={formRef}
      onFinish={onSubmit}
      initialValues={{
        pickup: shipmentData?.load?.pickUpTime ? moment.utc(shipmentData?.load?.pickUpTime).locale("en").local() : null,
        pickupEtaDate: shipmentData?.load?.pickupETA ? moment.utc(shipmentData?.load?.pickupETA) : null,
        pickupEtaTimeSlotIndex: parseInitialEtaTime(
          shipmentData?.load?.pickupETA,
          shipmentData?.load?.pickupETATimeSlot,
        ),
        dropoff: shipmentData?.load?.dropOffTime
          ? moment.utc(shipmentData?.load?.dropOffTime).locale("en").local()
          : null,
        dropoffEtaDate: shipmentData?.load?.dropOffETA ? moment.utc(shipmentData?.load?.dropOffETA) : null,
        dropoffEtaTimeSlotIndex: parseInitialEtaTime(
          shipmentData?.load?.dropOffETA,
          shipmentData?.load?.dropOffETATimeSlot,
        ),
      }}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="pickupEtaDate"
            label="Pickup ETA"
            style={{
              width: "100%",
            }}
          >
            <DatePicker
              format="MM/DD/YYYY"
              onChange={handlePickupEtaDateChange}
              disabledDate={disableDatesBeforeToday}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          <Form.Item
            name="pickupEtaTimeSlotIndex"
            style={{
              width: "100%",
            }}
          >
            <Select
              placeholder="Select Time"
              disabled={!pickupEtaDateSet}
              style={{
                width: "100%",
              }}
            >
              {Object.values(LoadTimeSlot).map(i => (
                <Select.Option value={Number(i)} key={Number(i)}>
                  {LoadTimeSlotMap[i]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="dropoffEtaDate"
            label="Drop-off ETA"
            style={{
              width: "100%",
            }}
          >
            <DatePicker
              format="MM/DD/YYYY"
              onChange={handleDropoffEtaDateChange}
              disabledDate={disableDatesBeforeToday}
              style={{
                width: "100%",
              }}
            />
          </Form.Item>

          <Form.Item
            name="dropoffEtaTimeSlotIndex"
            style={{
              width: "100%",
            }}
          >
            <Select
              placeholder="Select Time"
              disabled={!dropoffEtaDateSet}
              style={{
                width: "100%",
              }}
            >
              {Object.values(LoadTimeSlot).map(i => (
                <Select.Option value={Number(i)} key={Number(i)}>
                  {LoadTimeSlotMap[i]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
